// ========================================
// ERROR BOUNDARY FALLBACK COMPONENT
// ========================================
// Enhanced fallback component for React Error Boundaries

import React from 'react'
import { SUPPORT_EMAIL } from '../../../constants/routes'
import { ErrorPage } from './ErrorPage'
import { StandardError, ErrorCategory } from '../../../types/errorTypes'
import { errorService } from '../../../services/errorService'

interface ErrorBoundaryFallbackProps {
  error: Error
  resetErrorBoundary: () => void
  componentStack?: string
  eventId?: string
}

export function ErrorBoundaryFallback({
  error,
  resetErrorBoundary,
  componentStack,
  eventId,
}: ErrorBoundaryFallbackProps) {
  // Convert the boundary error to a StandardError
  const standardError: StandardError = React.useMemo(() => {
    return errorService.processGenericError(error, {
      component: 'ErrorBoundary',
      componentStack,
      eventId,
    })
  }, [error, componentStack, eventId])

  const handleRetry = () => {
    resetErrorBoundary()
  }

  const handleContactSupport = () => {
    const emailBody = `
Error Report:

Error ID: ${standardError.id}
Component: React Error Boundary
Event ID: ${eventId || 'N/A'}
Timestamp: ${standardError.context.timestamp}
URL: ${window.location.href}
User Agent: ${navigator.userAgent}

Error Details:
- Message: ${error.message}
- Name: ${error.name}
- Category: ${standardError.category}
- Severity: ${standardError.severity}

${componentStack ? `Component Stack:\n${componentStack}` : ''}

${error.stack ? `Stack Trace:\n${error.stack}` : ''}
    `.trim()

    window.open(
      `mailto:${SUPPORT_EMAIL}?subject=Error Boundary Report&body=${encodeURIComponent(emailBody)}`
    )
  }

  // Determine if this is a chunk loading error
  const isChunkLoadError =
    error.name === 'ChunkLoadError' ||
    error.message?.includes('Loading chunk') ||
    error.message?.includes('Loading CSS chunk')

  if (isChunkLoadError) {
    return (
      <ErrorPage
        error={{
          ...standardError,
          category: ErrorCategory.CHUNK_LOAD,
          userMessage:
            'Failed to load application resources. This usually happens after an update.',
        }}
        onRetry={() => window.location.reload()}
        onContactSupport={handleContactSupport}
        showTechnicalDetails={true}
      />
    )
  }

  return (
    <ErrorPage
      error={standardError}
      onRetry={handleRetry}
      onContactSupport={handleContactSupport}
      showTechnicalDetails={true}
    >
      {/* Additional context for React errors */}
      {componentStack && import.meta.env.DEV && (
        <details className="rounded-lg border bg-muted p-4 text-sm">
          <summary className="cursor-pointer font-medium">
            React Component Stack (Development)
          </summary>
          <pre className="mt-2 text-xs bg-background border rounded p-2 overflow-auto max-h-32">
            {componentStack}
          </pre>
        </details>
      )}
    </ErrorPage>
  )
}

export default ErrorBoundaryFallback
